import React, { useEffect, useRef, useState } from 'react';
import ReactMapGL, { Layer, Source } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Title from './Title';
import circle from "@turf/circle"
import { point } from "@turf/helpers"
import distance from "@turf/distance"
import SplitPane from 'react-split-pane';
import "../stylesheets/resizer.css"
import { useMemo } from 'react';
import FeaturesList from './geocode_tools/FeaturesList';
import { Button, Col, Form, Row } from 'react-bootstrap';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';

const geocodeDataFetcher = ({ radius, center, onSuccess }) => {
  const [longitude, latitude] = center
  let url = new URL('/v1/geocode', document.location)
  url.search = new URLSearchParams({ radius: 1000 * radius, longitude, latitude })
  fetch(url).then(function (response) {
    var contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json().then(onSuccess)
    } else {
      console.log("Oops, nous n'avons pas du JSON!");
    }
  });
}
const radius = (first, last) => (Math.min(distance(point(first), point(last)), 60 / 1000))
const DrawTools = ({ mode, setMode }) => {
  const selectionClick = (e) => {
    if (mode == "selection") {
      setMode("normal")
    } else {
      setMode("selection")
    }
  }

  return (
    <div className="mapboxgl-ctrl-top-left">
      <div className="mapboxgl-ctrl-group mapboxgl-ctrl">
        <button
          type="button"
          className={`mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_circle ${mode === "selection" ? 'selected' : ''}`}
          title="Selection"
          onClick={selectionClick}
        />
      </div>
    </div>
  )
}
const DrawCircleLayer = ({ circleProperties, setCircleProperties }) => {
  const [drawnCircle, setDrawnCircle] = useState()
  const toCircle = () => {
    if (circleProperties.center, circleProperties.radius) {
      return circle(circleProperties.center, circleProperties.radius, { steps: 32 })
    }
    return null
  }
  const meterRoundString = () => (
    JSON.stringify({ center: circleProperties.center, radius: Math.round(circleProperties.radius * 1000) / 1000 })
  )

  useEffect(() => {
    setDrawnCircle(toCircle())
  }, [meterRoundString()])

  return (
    <Source id="selection" type="geojson" data={drawnCircle}>
      <Layer
        id="selection_layer"
        type="fill"
        paint={{
          'fill-color': "grey",
          "fill-opacity": 0.2,
          "fill-outline-color": "grey"
        }}
      />
    </Source>
  )
}
const ResultLayer = ({ data, mapRef, highlightIds }) => {
  const geojsonExtractPolygons = (geojson) => (
    {
      "type": "FeatureCollection",
      "features": geojson.features.filter((f) => (f.geometry.type === "Polygon"))
    }
  )
  const geojsonExtractPoints = (geojson) => (
    {
      "type": "FeatureCollection",
      "features": geojson.features.filter((f) => (f.geometry.type === "Point"))
    }
  )
  const polyGeojson = useMemo(() => (geojsonExtractPolygons(data)), [JSON.stringify(data)])
  const pointGeojson = useMemo(() => (geojsonExtractPoints(data)), [JSON.stringify(data)])
  const setHighlights = (ids) => {
    data.features.forEach((feature) => {
      const source = feature.geometry.type == "Point" ? "result_point" : "result_polygon"
      mapRef.current.getMap().setFeatureState(
        { source: source, id: feature.properties.id },
        { highlight: ids.includes(feature.properties.id) }
      )
    })
  }
  useEffect(() => {
    setHighlights(highlightIds)
  }, [highlightIds])

  return (
    <>
      <Source id="result_polygon" type="geojson" data={polyGeojson} promoteId={"id"}>
        <Layer
          id="result_layer_polygon"
          type="fill"
          paint={{
            'fill-color': {
              property: "probability",
              stops: [
                [0.1, '#3288bd'],
                [0.2, '#66c2a5'],
                [0.3, '#abdda4'],
                [0.4, '#e6f598'],
                [0.5, '#ffffbf'],
                [0.6, '#fee08b'],
                [0.7, '#fdae61'],
                [0.8, '#f46d43'],
                [0.9, '#d53e4f']
              ]
            },
            "fill-opacity": [
              'case',
              ["boolean", ["feature-state", "highlight"], false],
              0.9,
              0.6,
            ],
            "fill-outline-color": [
              'case',
              ["boolean", ["feature-state", "highlight"], false],
              'black',
              'grey',
            ]
          }}
        />
      </Source>
      <Source id="result_point" type="geojson" data={pointGeojson} promoteId={"id"}>
        <Layer
          id="result_layer_point"
          type="circle"
          paint={{
            'circle-radius': 5,
            'circle-color': {
              property: "probability",
              stops: [
                [0.1, '#3288bd'],
                [0.2, '#66c2a5'],
                [0.3, '#abdda4'],
                [0.4, '#e6f598'],
                [0.5, '#ffffbf'],
                [0.6, '#fee08b'],
                [0.7, '#fdae61'],
                [0.8, '#f46d43'],
                [0.9, '#d53e4f']
              ]
            },
            "circle-stroke-color": [
              'case',
              ["boolean", ["feature-state", "highlight"], false],
              'black',
              'grey',
            ],
            "circle-stroke-width": [
              'case',
              ["boolean", ["feature-state", "highlight"], false],
              3,
              1,
            ]
          }}
        />
      </Source>
    </>
  )
}

const ManualInput = ({ value, onChange }) => {
  const [longitude, setLongitude] = useState("")
  const [latitude, setLatitude] = useState("")
  const [radius, setRadius] = useState("")

  useEffect(() => {
    setLongitude(get(value, 'center[0]', '') || "")
    setLatitude(get(value, 'center[1]', '') || "")
    setRadius(get(value, 'radius', '') || "")
  }, [value])

  const onSubmit = (e) => {
    e.preventDefault()
    onChange({longitude, latitude, radius})
  }

  const canSubmit = () => {
    const {onDraw, endPoint, ...valueForCompare} = mapValues(value, v => v || '')
    return !isEqual(valueForCompare, {center: [longitude, latitude], radius})
  }

  return (
    <div style={{width: "100%", height:"100%", overflowY:"auto", overflowX:"hidden", marginLeft: "20px"}}>
      <Form onSubmit={onSubmit}>
        <Form.Group as={Row} controlId="longitude">
          <Form.Label column sm="2">
            longitude
          </Form.Label>
          <Col sm="10">
            <Form.Control type="number" value={longitude} onChange={(e)=>{setLongitude(e.target.value)}} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="latitude">
          <Form.Label column sm="2">
            latitude
          </Form.Label>
          <Col sm="10">
            <Form.Control type="number" value={latitude} onChange={(e)=>{setLatitude(e.target.value)}} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="radius">
          <Form.Label column sm="2">
            radius
          </Form.Label>
          <Col sm="10">
            <Form.Control type="number" value={radius} onChange={(e)=>{setRadius(e.target.value)}} />
          </Col>
        </Form.Group>
        {canSubmit() && <Button variant="primary" type="submit">
          Submit
        </Button>}
      </Form>
    </div>
  )
}

const GeocodeTool = () => {
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    width: "100%",
    height: "75vh",
    latitude: 48.863730,
    longitude: 2.357882,
    zoom: 11
  });
  const [mode, setMode] = useState("normal")
  const [circleProperties, setCircleProperties] = useState(
    {
      center: null,
      endPoint: null,
      onDraw: false,
      radius: null
    })
  const defaultJson = {
    "type": "FeatureCollection",
    "features": []
  }
  const [result, setResult] = useState(defaultJson)
  const onHover = (e) => {
    if (mode === "selection" && circleProperties.onDraw) {
      setCircleProperties({ ...circleProperties, radius: radius(circleProperties.center, e.lngLat), endPoint: e.lngLat })
    } else if (mode == "normal" && e.features && e.features.length > 0) {
      setHighlightIds(e.features.map((elt) => (elt.id)))
    } else {
      setHighlightIds([])
    }
  }
  const onClickDraw = (e) => {
    if (!circleProperties.onDraw) {
      setCircleProperties({ ...circleProperties, center: e.lngLat, radius: 0, endPoint: null, onDraw: true })
    } else {
      setCircleProperties({ ...circleProperties, radius: radius(circleProperties.center, e.lngLat), endPoint: e.lngLat, onDraw: false })
      setMode("normal")
    }
  }
  const onClick = (e) => {
    if (e.target.nodeName == "BUTTON") { return }
    if (mode === "selection") {
      onClickDraw(e)
    }
  }
  const getCursor = (e) => {
    if (mode === "selection") { return "crosshair" }
    if (e.isDragging) { return "grabbing" }
    if (e.isHovering) { return "pointer" }
    return "grab"
  }
  const [mapWidth, setMapWidth] = useState("100%")
  useEffect(() => {
    setViewport({ ...viewport, width: mapWidth })
  }, [mapWidth])
  useEffect(() => {
    if (!circleProperties.onDraw && circleProperties.center && circleProperties.endPoint)
      geocodeDataFetcher({
        ...circleProperties, onSuccess: (json) => {
          setResult(json)
        }
      })
  }, [JSON.stringify(circleProperties)])

  const onLeftResize = (size) => {
    setMapWidth(`${size}px`)
  }
  const [highlightIds, setHighlightIds] = useState(null)
  const onSubmit = (e) => {
    setCircleProperties({...circleProperties, center: [e.longitude, e.latitude], radius: e.radius})
  }

  return (<>
    <Title main={"Geocode Tool"}>
    </Title>
    <div style={{ width: "100%", height: "75vh", position: "relative" }}>
      <SplitPane split="vertical" minSize={"25%"} defaultSize={"75%"} onChange={onLeftResize}>
        <ReactMapGL
          ref={mapRef}
          getCursor={getCursor}
          mapboxApiAccessToken="pk.eyJ1Ijoic2luZ2xlc3BvdCIsImEiOiJja2x5NzAwbngzNzZ2MndxbTEwcWMzaXoxIn0.P5ONBWqu0EQck_vMzCek-w"
          mapStyle="mapbox://styles/singlespot/ckmuel29v0g7o17mzo8hr0fwp"
          onViewportChange={(viewport) => setViewport(viewport)}
          onClick={onClick}
          onHover={onHover}
          interactiveLayerIds={["result_layer_polygon", "result_layer_point"]}
          {...viewport}
        >
          <DrawTools mode={mode} setMode={setMode} />
          <DrawCircleLayer circleProperties={circleProperties} setCircleProperties={setCircleProperties} />
          <ResultLayer
            data={result}
            mapRef={mapRef}
            highlightIds={highlightIds} />
        </ReactMapGL>
        <SplitPane split="horizontal" defaultSize={"220px"}>
          <ManualInput
            value={circleProperties}
            onChange={onSubmit}
          />
          <FeaturesList
            features={result}
            onHover={(e) => { setHighlightIds([e.properties.id]) }}
            highlightIds={highlightIds}
          />
        </SplitPane>
      </SplitPane>
    </div>
  </>);
}

export default GeocodeTool;
